import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../Context/LanguageContext'
import { WEB_BASE_URL } from '../../Functions/imigoWebConfig'

/**
 * SEO Component for dynamic meta tag management with multilingual support
 * Usage: <SEO title="Page Title" description="Page description" />
 */
const SEO = ({
  title,
  description,
  keywords,
  image = '/assets/pic/HomePic.jpg',
  url,
  type = 'website',
  author = 'Hani Eisapur',
  publishedTime,
  modifiedTime,
  structuredData,
}) => {
  const location = useLocation();
  const { language, t, getCurrentPath } = useLanguage();
  const baseUrl = WEB_BASE_URL;
  
  // Use translations if title/description/keywords not provided
  const seoTitle = title || t('seo.defaultTitle');
  const seoDescription = description || t('seo.defaultDescription');
  const seoKeywords = keywords || t('seo.defaultKeywords');
  
  // Get current path without language prefix for canonical
  const currentPath = getCurrentPath();
  const currentUrl = url || `${baseUrl}${location.pathname}`;
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  
  // Locale mapping for Open Graph
  const localeMap = {
    en: 'en_US',
    ar: 'ar_SA',
    fa: 'fa_IR',
  };
  
  const currentLocale = localeMap[language] || 'en_US';

  useEffect(() => {
    // Update document title
    document.title = seoTitle;
    
    // Update HTML lang attribute
    document.documentElement.lang = language;

    // Helper function to update or create meta tag
    const updateMetaTag = (name, content, attribute = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper function to update or create link tag
    const updateLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic meta tags
    updateMetaTag('description', seoDescription);
    updateMetaTag('keywords', seoKeywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // Canonical URL (without language prefix for SEO)
    const canonicalPath = currentPath === '/' ? '' : currentPath;
    updateLinkTag('canonical', `${baseUrl}${canonicalPath}`);

    // Open Graph tags
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:url', currentUrl, 'property');
    updateMetaTag('og:title', seoTitle, 'property');
    updateMetaTag('og:description', seoDescription, 'property');
    updateMetaTag('og:image', fullImageUrl, 'property');
    updateMetaTag('og:image:width', '1200', 'property');
    updateMetaTag('og:image:height', '630', 'property');
    updateMetaTag('og:image:alt', seoTitle, 'property');
    updateMetaTag('og:site_name', 'Imigo', 'property');
    updateMetaTag('og:locale', currentLocale, 'property');
    
    // Add alternate language links (hreflang) for multilingual SEO
    const supportedLanguages = ['en', 'ar', 'fa'];
    supportedLanguages.forEach((lang) => {
      const langPath = lang === 'en' ? currentPath : `/${lang}${currentPath}`;
      const langUrl = `${baseUrl}${langPath}`;
      const langLocale = localeMap[lang] || 'en_US';
      
      // Remove existing hreflang link if any
      const existingLink = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
      if (existingLink) {
        existingLink.remove();
      }
      
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', langUrl);
      document.head.appendChild(link);
    });
    
    // Add x-default hreflang (points to English version)
    const defaultPath = currentPath;
    const defaultUrl = `${baseUrl}${defaultPath}`;
    const existingDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (existingDefault) {
      existingDefault.remove();
    }
    const defaultLink = document.createElement('link');
    defaultLink.setAttribute('rel', 'alternate');
    defaultLink.setAttribute('hreflang', 'x-default');
    defaultLink.setAttribute('href', defaultUrl);
    document.head.appendChild(defaultLink);

    if (publishedTime) {
      updateMetaTag('og:published_time', publishedTime, 'property');
    }
    if (modifiedTime) {
      updateMetaTag('og:modified_time', modifiedTime, 'property');
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', currentUrl);
    updateMetaTag('twitter:title', seoTitle);
    updateMetaTag('twitter:description', seoDescription);
    updateMetaTag('twitter:image', fullImageUrl);
    updateMetaTag('twitter:image:alt', seoTitle);

    // Article specific tags (if type is article)
    if (type === 'article') {
      if (publishedTime) {
        updateMetaTag('article:published_time', publishedTime, 'property');
      }
      if (modifiedTime) {
        updateMetaTag('article:modified_time', modifiedTime, 'property');
      }
    }

    // Structured Data (JSON-LD)
    if (structuredData) {
      // Remove existing structured data script if any
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [seoTitle, seoDescription, seoKeywords, image, currentUrl, type, author, publishedTime, modifiedTime, structuredData, fullImageUrl, language, currentPath, currentLocale, baseUrl]);

  return null;
};

export default SEO;

